import { Box } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Option } from '@spike/model';
import useNonInitialEffect from '@versiondos/hooks';
import { createDepositInvoiceThunk, getInvoiceThunk } from '@spike/invoices-action';
import { confirmDepositThunk, getEnrollmentStatusThunk } from '@spike/payments-action';
import { cancelThunk as cancelPosThunk } from '@spike/pos-action';
import PaymentMethods from 'components/Checkout2/PaymentMethods';
import Invoice from '@spike/invoice-model';
import { EnrollmentStatus, PaymentMethodIds } from '@spike/payments-model';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InvoicesStatus } from '@spike/invoices-action';
import { PaymentsStatus } from '@spike/payments-action';
import { RootState } from 'store';
import { useApiClientWrapper } from 'hooks';
import FooterComponent from '../FooterComponent';
import PosPayment from './PosPayment';
import clsx from 'clsx';
import CreditCardPayment from 'components/Checkout2/CreditCardPayment2';
import { Button, Spinner } from 'components/UI';

interface CollectDepositProps {
    bookingId: number;
    onStartBooking?: () => void;
    onCollected?: (paidAmount: number) => void;
    onClose?: () => void;
    /** Counter staff flow only: show Skip & Book beside Pay & Book. OLB / collect-from-order should pass false. */
    allowSkipAndBook?: boolean;
    onSkipAndBook?: () => void;
    /**
     * When collecting deposit from booking detail, pass the order's checkout invoice id.
     * Loads that invoice via GET instead of POST create_deposit_invoice — avoids duplicating deposit rows/amounts.
     */
    orderCheckoutInvoiceId?: number;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        paymentBackground: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            zIndex: 10,
            display: 'flex',
            width: '100%',
            minHeight: '100%'
        },
        paymentContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        paymentContent: {
            padding: '0px 29px 32px',
            [theme.breakpoints.up('md')]: {
                padding: '0px 32px 35px'
            }
        },
        expanded: {
            height: '100%'
        },
        sectionMargin: {
            height: '85%',
            padding: `0px 30px 33px`,
            [theme.breakpoints.up('md')]: {
                padding: '0px 32px 35px'
            }
        },
        sectionMarginPos: {
            height: '85%'
        },
        title: {
            fontWeight: 600,
            fontSize: 24
        },
        totalContainer: {
            height: '15%',
            borderTop: '1px solid #D4D4D4',
            marginTop: 10,
            marginBottom: 38
        },
        skipButtonContainer: {
            margin: '16px 36px 13px 36px',
            [theme.breakpoints.up('md')]: {
                margin: '21px 40px 0px 40px'
            }
        },
        skipButton: {
            height: 50,
            width: '100%',
            [theme.breakpoints.down('sm')]: {
                borderRadius: 30
            },
            [theme.breakpoints.up('md')]: {
                height: 55
            }
        },
        paymentMethods: {
            '& .MuiTypography-body1': {
                fontSize: 17
            },
            '& .MuiBox-root': {
                height: 93,
                width: '100%'
            }
        },
        pos: {
            'height': '100%',
            '& .MuiTypography-body1': {
                fontSize: 17
            }
        }
    })
);

interface CheckoutData {
    paymentMethodId: string | null;
    posTerminalId: number | null;
    posServiceId: string | null;
}

export const CollectDeposit: FunctionComponent<CollectDepositProps> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const loadedInvoiceKey = useRef<string | null>(null);

    const depositInvoiceFromStore = useSelector<RootState, Invoice | undefined>(state => state.invoices.depositInvoice);
    const invoiceFromOrder = useSelector<RootState, Invoice | undefined>(state => state.invoices.invoice);
    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);

    const invoiceForDeposit = useMemo(() => {
        if (props.orderCheckoutInvoiceId != null) {
            return invoiceFromOrder?.id === props.orderCheckoutInvoiceId ? invoiceFromOrder : undefined;
        }
        return depositInvoiceFromStore;
    }, [props.orderCheckoutInvoiceId, invoiceFromOrder, depositInvoiceFromStore]);

    const depositUnpaidAmount = invoiceForDeposit?.deposits?.unpaidTotal
        ? Number(invoiceForDeposit.deposits.unpaidTotal)
        : 0;

    const enrollmentStatus = useSelector<RootState, EnrollmentStatus | undefined>(
        state => state.payments.enrollmentStatus
    );
    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);

    const [checkoutData, setCheckoutData] = useState<CheckoutData>({
        paymentMethodId: null,
        posTerminalId: null,
        posServiceId: null
    });

    const [loading, setLoading] = useState({
        invoice: true,
        paymentStatus: true,
        confirmDeposit: false
    });

    useEffect(() => {
        const invoiceKey =
            props.orderCheckoutInvoiceId != null
                ? `order:${props.orderCheckoutInvoiceId}`
                : `booking:${props.bookingId}`;

        if (loadedInvoiceKey.current === invoiceKey) {
            return;
        }

        loadedInvoiceKey.current = invoiceKey;
        setLoading(prev => ({ ...prev, invoice: true, paymentStatus: true }));
        dispatch(getEnrollmentStatusThunk(apiClientWrapper));
        if (props.orderCheckoutInvoiceId != null) {
            dispatch(getInvoiceThunk(apiClientWrapper, props.orderCheckoutInvoiceId));
        } else {
            dispatch(createDepositInvoiceThunk(apiClientWrapper, props.bookingId));
        }
    }, [apiClientWrapper, dispatch, props.bookingId, props.orderCheckoutInvoiceId]);

    useEffect(() => {
        if (props.orderCheckoutInvoiceId == null) return;
        if (invoiceFromOrder?.id === props.orderCheckoutInvoiceId && invoiceFromOrder.deposits?.unpaidTotal != null) {
            setLoading(prev => ({ ...prev, invoice: false }));
        }
    }, [props.orderCheckoutInvoiceId, invoiceFromOrder?.id, invoiceFromOrder?.deposits?.unpaidTotal]);

    useNonInitialEffect(() => {
        switch (paymentsStatus) {
            case PaymentsStatus.GetEnrollmentStatusSuccess:
                setLoading(prev => ({ ...prev, paymentStatus: false }));
                break;
            case PaymentsStatus.ConfirmingDeposit:
                props.onStartBooking && props.onStartBooking();
                break;
            case PaymentsStatus.ConfirmDepositSuccess:
                setLoading(prev => ({ ...prev, confirmDeposit: false }));
                props.onCollected &&
                    invoiceForDeposit &&
                    props.onCollected(Number(invoiceForDeposit?.deposits.unpaidTotal));
                break;
            case PaymentsStatus.Error:
                setLoading(prev => ({
                    ...prev,
                    paymentStatus: false,
                    confirmDeposit: false
                }));
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentsStatus]);

    useNonInitialEffect(() => {
        switch (invoicesStatus) {
            case InvoicesStatus.CreateDepositInvoiceSuccess:
                if (props.orderCheckoutInvoiceId == null) {
                    setLoading(prev => ({ ...prev, invoice: false }));
                }
                break;
            case InvoicesStatus.GetInvoiceSuccess:
                if (props.orderCheckoutInvoiceId != null) {
                    setLoading(prev => ({ ...prev, invoice: false }));
                }
                break;
            case InvoicesStatus.Error:
                setLoading(prev => ({ ...prev, invoice: false }));
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoicesStatus, props.orderCheckoutInvoiceId]);

    const confirmDeposit = (invoiceId: number, paymentMethodId: string, sessionId?: string) => {
        setLoading(prev => ({ ...prev, confirmDeposit: true }));
        dispatch(confirmDepositThunk(apiClientWrapper, invoiceId, paymentMethodId, sessionId));
    };

    const collectCreditCardHandler = (paidAmount: number) => {
        props.onCollected && props.onCollected(paidAmount);
    };

    const collectPOSHandler = () => {
        props.onCollected && invoiceForDeposit && props.onCollected(Number(invoiceForDeposit?.deposits.unpaidTotal));
    };

    const collectHandler = () => {
        if (!invoiceForDeposit?.id) return;
        confirmDeposit(invoiceForDeposit.id, checkoutData.paymentMethodId!);
    };

    const cancelPos = () => {
        if (!invoiceForDeposit?.id || !checkoutData.posServiceId || !checkoutData.posTerminalId) {
            return;
        }
        dispatch(
            cancelPosThunk(
                apiClientWrapper,
                invoiceForDeposit.id,
                checkoutData.posTerminalId,
                checkoutData.posServiceId
            )
        );
    };

    const skipAndBookHandler = () => {
        cancelPos();
        props.onSkipAndBook && props.onSkipAndBook();
    };

    const allowSkip = Boolean(props.allowSkipAndBook && props.onSkipAndBook);

    const changePaymentMethod = (option: Option<string>) => {
        cancelPos();
        setCheckoutData(prev => ({
            ...prev,
            paymentMethodId: option.id,
            posTerminalId: null,
            posServiceId: null
        }));
    };

    return (
        <Box className={classes.paymentBackground}>
            <Box className={classes.paymentContainer}>
                <Box
                    className={clsx(classes.paymentContent, {
                        [classes.expanded]:
                            checkoutData.paymentMethodId === PaymentMethodIds.Other ||
                            checkoutData.paymentMethodId === PaymentMethodIds.Cash
                    })}
                >
                    {!loading.invoice && !loading.paymentStatus && (
                        <PaymentMethods
                            paymentsEnabled={enrollmentStatus?.enabled || false}
                            selectedId={checkoutData.paymentMethodId}
                            className={classes.paymentMethods}
                            onSelect={changePaymentMethod}
                            source={'deposit'}
                        />
                    )}
                    {(loading.invoice || loading.paymentStatus) && <Spinner />}
                </Box>

                {checkoutData.paymentMethodId === PaymentMethodIds.CreditCard && invoiceForDeposit?.id && (
                    <Box className={classes.sectionMargin}>
                        <CreditCardPayment
                            invoiceUuid={invoiceForDeposit!.uuid!}
                            invoiceId={invoiceForDeposit.id}
                            clientId={invoiceForDeposit!.customer!.id!}
                            depositAmount={Number(invoiceForDeposit!.deposits.unpaidTotal!)}
                            onSuccess={collectCreditCardHandler}
                        />
                        {allowSkip && (
                            <Box className={classes.totalContainer}>
                                <Box className={classes.skipButtonContainer}>
                                    <Button
                                        id="booking_button_skip_secondary"
                                        label="Skip & Book"
                                        loading={false}
                                        color="black"
                                        size="large"
                                        variant="secondary"
                                        className={classes.skipButton}
                                        onClick={skipAndBookHandler}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {checkoutData.paymentMethodId === PaymentMethodIds.POS && invoiceForDeposit?.id && (
                    <Box className={classes.sectionMarginPos}>
                        <PosPayment
                            depositInvoiceId={invoiceForDeposit.id}
                            footerDepositTotal={depositUnpaidAmount}
                            onSuccess={collectPOSHandler}
                            onGetPosServiceId={(posTerminalId, posServiceId) =>
                                setCheckoutData(prev => ({
                                    ...prev,
                                    posTerminalId,
                                    posServiceId
                                }))
                            }
                            allowSkipAndBook={allowSkip}
                            onSkipAndBook={skipAndBookHandler}
                            className={classes.pos}
                        />
                    </Box>
                )}

                {[PaymentMethodIds.Cash.toString(), PaymentMethodIds.Other.toString()].includes(
                    checkoutData.paymentMethodId || ''
                ) &&
                    invoiceForDeposit?.id && (
                        <Box className={classes.totalContainer}>
                            <FooterComponent
                                title="Deposit"
                                total={depositUnpaidAmount}
                                loading={loading.confirmDeposit}
                                buttonText="Pay & Book"
                                secondaryButtonText={allowSkip ? 'Skip & Book' : undefined}
                                onSecondaryBook={allowSkip ? skipAndBookHandler : undefined}
                                onBook={collectHandler}
                            />
                        </Box>
                    )}
            </Box>
        </Box>
    );
};

export default CollectDeposit;
